// packages/ops-platform-v3/src/frontend/expoams/TransactionHistory.tsx
//
// Ported from StationJo's src/components/expoams/TransactionHistory.tsx
// (Plan 09 Task 10).
// - Both check-in and check-out histories arrive in a single
//   `expoamsClient.getHistory` call (Plan 09 Task 15). StationJo's original
//   split-into-two-fetches pattern collapses here: one round trip, one
//   `{ checkIns, checkOuts }` response.
// - Types import swapped from '../../types/expoams' to './types.js' (siblings).
// - No Firebase Web SDK imports.
// - `noUncheckedIndexedAccess` is satisfied via local variable caching when
//   dereferencing sheetNames[id].

import React, { useState, useEffect, useRef } from 'react';
import { expoamsClient } from './services/index.js';

export interface TransactionHistoryProps {
  sheetIds: string[];
  sheetNames: Record<string, string>;
  selectedSheetId: string;
  onSelectSheet: (id: string) => void;
  /** Bumped by the router after a scan; invalidates the per-sheet cache. */
  dataVersion?: number;
}

type HistoryFilter = 'all' | 'check-in' | 'check-out';

interface UnifiedRecord {
  type: 'in' | 'out';
  item: string;
  quantity: number;
  location: string;
  name: string;
  dateTime: string;
  usedFor?: string;
}

export const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  sheetIds,
  sheetNames,
  selectedSheetId,
  onSelectSheet,
  dataVersion = 0,
}) => {
  const [records, setRecords] = useState<UnifiedRecord[]>([]);
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // History is the largest ExpoAMS payload (~1 MB for 8k rows); keep it per
  // sheet until a scan bumps dataVersion so tab switches don't re-download.
  const cacheRef = useRef<Map<string, UnifiedRecord[]>>(new Map());
  const cacheVersionRef = useRef(dataVersion);

  if (cacheVersionRef.current !== dataVersion) {
    cacheVersionRef.current = dataVersion;
    cacheRef.current.clear();
  }

  useEffect(() => {
    if (!selectedSheetId) return;
    const cached = cacheRef.current.get(selectedSheetId);
    if (cached) {
      setRecords(cached);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    expoamsClient
      .getHistory({ sheetId: selectedSheetId })
      .then(({ checkIns, checkOuts }) => {
        if (cancelled) return;
        const unified: UnifiedRecord[] = [
          ...checkIns.map(
            (r): UnifiedRecord => ({
              type: 'in',
              item: r.item,
              quantity: r.quantity,
              location: r.location,
              name: r.name,
              dateTime: r.dateTime,
            }),
          ),
          ...checkOuts.map(
            (r): UnifiedRecord => ({
              type: 'out',
              item: r.item,
              quantity: r.quantity,
              location: r.location,
              name: r.name,
              dateTime: r.dateTime,
              usedFor: r.usedFor,
            }),
          ),
        ];
        // Sort by dateTime descending (most recent first)
        unified.sort((a, b) => {
          const da = new Date(a.dateTime).getTime();
          const db = new Date(b.dateTime).getTime();
          return db - da;
        });
        cacheRef.current.set(selectedSheetId, unified);
        setRecords(unified);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load history';
        setError(message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSheetId, dataVersion]);

  const filtered = records.filter((r) => {
    if (filter === 'check-in' && r.type !== 'in') return false;
    if (filter === 'check-out' && r.type !== 'out') return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        r.item.toLowerCase().includes(s) ||
        r.name.toLowerCase().includes(s) ||
        r.location.toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div>
      <select
        className="expoams-sheet-select"
        value={selectedSheetId}
        onChange={(e) => onSelectSheet(e.target.value)}
        style={{ marginBottom: '0.75rem' }}
      >
        <option value="" disabled>
          — Select a sheet —
        </option>
        {sheetIds.map((id) => {
          // noUncheckedIndexedAccess: cache label locally (fallback to id).
          const label = sheetNames[id] ?? id;
          return (
            <option key={id} value={id}>
              {label}
            </option>
          );
        })}
      </select>

      {!selectedSheetId && (
        <div style={{ textAlign: 'center', color: '#888', margin: '1rem 0', fontSize: '0.9rem' }}>
          Select a sheet above to view history.
        </div>
      )}

      {/* Filter toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {(['all', 'check-in', 'check-out'] as HistoryFilter[]).map((f) => (
          <button
            key={f}
            className={`expoams-purpose-btn ${filter === f ? 'selected' : ''}`}
            style={{ flex: 1, padding: '0.5rem', fontSize: '0.85rem' }}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f === 'check-in' ? 'In' : 'Out'}
          </button>
        ))}
      </div>

      <input
        className="expoams-add-input"
        placeholder="Search item, name, or location..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: '100%', marginBottom: '0.75rem' }}
      />

      {error && <div className="expoams-error">{error}</div>}
      {loading && <div className="expoams-loading">Loading...</div>}

      {!loading &&
        filtered.slice(0, 100).map((r, i) => (
          <div key={i} className="expoams-history-card">
            <div className="expoams-history-card-header">
              <span>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1.5rem',
                    height: '1.5rem',
                    borderRadius: '50%',
                    textAlign: 'center',
                    lineHeight: '1.5rem',
                    fontSize: '0.75rem',
                    color: '#fff',
                    background: r.type === 'in' ? '#4caf50' : '#f44336',
                    marginRight: '0.5rem',
                  }}
                >
                  {r.type === 'in' ? '↓' : '↑'}
                </span>
                {r.item}
              </span>
              <span style={{ fontWeight: 600 }}>x{r.quantity}</span>
            </div>
            <div className="expoams-history-card-detail">
              📍 {r.location} · {r.name}
              {r.usedFor && ` · ${r.usedFor}`}
            </div>
            <div
              className="expoams-history-card-detail"
              style={{ fontSize: '0.8rem', color: '#999' }}
            >
              {r.dateTime}
            </div>
          </div>
        ))}

      {!loading && filtered.length > 100 && (
        <div className="expoams-loading">Showing first 100 of {filtered.length} records</div>
      )}
    </div>
  );
};

export default TransactionHistory;
